/**
 * @fileoverview Sales channel selection and integration step for onboarding.
 * Allows users to choose between Shopify (direct) or BaseLinker (OMS) for order ingestion.
 * @module features/onboarding/components/steps/SalesChannelStep
 */

import { ReactElement, useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  ArrowLeft,
  ArrowRight,
  ExternalLink,
  Eye,
  EyeOff,
  Loader2,
  Search,
} from 'lucide-react'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { ScrollReveal } from '@/shared/components/ui'
import { useActiveOrg } from '@/features/orgs/context/ActiveOrgProvider'
import { useIntegrationsStatus } from '@/features/settings/hooks/useIntegrationsStatus'
import {
  getBaselinkerCurrency,
  setBaselinkerCurrency,
} from '@/features/settings/api/baselinkerCurrencyService'
import { getOrgSettings } from '@/features/settings/api/orgSettingsService'
import { OrgIdSchema } from '@/features/orgs/schemas/orgSchemas'
import apiClient from '@/lib/apiClient'
import {
  useBaselinkerInventoriesQuery,
  useImportPreviewQuery,
  useImportProductsMutation,
} from '@/features/baselinker-import/api/baselinkerImportService'
import { BaselinkerProductsTable } from '@/features/baselinker-import/components/BaselinkerProductsTable'
import {
  listStatusMappings,
  updateStatusMapping,
  type ViewType,
} from '@/features/status-mappings/api/statusMappingsService'
import { ShopifyOAuthConnect } from '@/features/settings/components/ShopifyOAuthConnect'
import { useUpdateConnectionModeMutation, useStoresQuery } from '@/features/settings/api/storeService'
import {
  useShopifyStoresQuery,
  useShopifyImportPreviewQuery,
  useShopifyImportProductsMutation,
} from '@/features/shopify-import/api/shopifyImportService'
import {
  ProductImportTable,
  type ImportProductItem,
  type ImportVariantItem,
} from '@/shared/components/ProductImportTable'
import type { ShopifyVariantPreviewItem, ShopifyProductPreviewItem } from '@/features/shopify-import/schemas/shopifyImportSchema'

/** Sub-step types for the sales channel wizard */
type SalesChannelSubStep =
  | 'store-connect'        // Connect Shopify store (ATTRIBUTION_ONLY) or skip
  | 'order-source-choice'  // Choose order source: Direct Shopify or BaseLinker OMS
  // Shopify direct flow
  | 'shopify-import'
  // Baselinker flow
  | 'baselinker-guide'
  | 'baselinker-connect'
  | 'baselinker-currency'
  | 'baselinker-import'
  | 'baselinker-mappings'

const BaseLinkerCredentialsSchema = z.object({
  blApiToken: z.string().min(1, 'API token is required'),
})

interface SalesChannelStepProps {
  /** Callback when sales channel setup is complete */
  onComplete: () => void
  /** Callback to skip this step */
  onSkip: () => void
}

/**
 * Sales channel selection step for onboarding.
 * Users first connect their store (Shopify) for ad attribution,
 * then choose their order source (Direct from Shopify or OMS via BaseLinker).
 *
 * @component
 */
export function SalesChannelStep({ onComplete, onSkip }: SalesChannelStepProps): ReactElement {
  const { activeOrgId } = useActiveOrg()

  const [subStep, setSubStep] = useState<SalesChannelSubStep>('store-connect')

  // Check existing integrations to auto-skip
  const integrationsQuery = useIntegrationsStatus()
  const baselinkerStatus = integrationsQuery.data?.find(i => i.provider === 'BASELINKER')
  const isBaselinkerConnected = baselinkerStatus?.status === 'ACTIVE'

  // Check for any existing Shopify store (any connection mode)
  const storesQuery = useStoresQuery()
  const shopifyStore = storesQuery.data?.find(s => s.platformType === 'SHOPIFY' && s.integrationId)
  const hasShopifyStore = !!shopifyStore

  // Check for existing Shopify DIRECT connections (for import step)
  const shopifyStoresQuery = useShopifyStoresQuery(
    subStep === 'shopify-import' || subStep === 'order-source-choice'
  )
  const hasShopifyDirect = (shopifyStoresQuery.data?.length ?? 0) > 0

  // Determine effective sub-step (auto-skip if already connected)
  const effectiveSubStep = useMemo(() => {
    if (subStep === 'store-connect') {
      // If store already connected, skip to order source choice
      if (hasShopifyStore) return 'order-source-choice'
    }
    if (subStep === 'order-source-choice') {
      // If already has DIRECT store AND BaseLinker, show the choice
      if (hasShopifyDirect && isBaselinkerConnected) return 'order-source-choice'
      // If already has DIRECT store, go to product import
      if (hasShopifyDirect) return 'shopify-import'
      // If BaseLinker is already connected, skip to currency
      if (isBaselinkerConnected) return 'baselinker-currency'
    }
    if (subStep === 'baselinker-guide' && isBaselinkerConnected) return 'baselinker-currency'
    return subStep
  }, [subStep, hasShopifyStore, hasShopifyDirect, isBaselinkerConnected])

  // Render based on effective sub-step
  switch (effectiveSubStep) {
    case 'store-connect':
      return (
        <StoreConnectScreen
          onConnected={() => setSubStep('order-source-choice')}
          onSkip={() => setSubStep('order-source-choice')}
        />
      )
    case 'order-source-choice':
      return (
        <OrderSourceChoice
          hasShopifyStore={hasShopifyStore}
          shopifyStoreId={shopifyStore?.id ?? null}
          onSelectDirect={() => setSubStep('shopify-import')}
          onSelectBaselinker={() => setSubStep('baselinker-guide')}
          onBack={() => setSubStep('store-connect')}
          onSkip={onSkip}
        />
      )
    case 'shopify-import':
      return (
        <ShopifyImportSubStep
          onComplete={onComplete}
          onBack={() => setSubStep('order-source-choice')}
          onSkip={onComplete}
        />
      )
    case 'baselinker-guide':
      return (
        <BaselinkerGuideSubStep
          onContinue={() => setSubStep('baselinker-connect')}
          onBack={() => setSubStep('order-source-choice')}
          onSkip={onSkip}
        />
      )
    case 'baselinker-connect':
      return (
        <BaselinkerConnectSubStep
          onConnected={() => setSubStep('baselinker-currency')}
          onBack={() => setSubStep('baselinker-guide')}
        />
      )
    case 'baselinker-currency':
      return (
        <BaselinkerCurrencySubStep
          activeOrgId={activeOrgId ?? undefined}
          onSaved={() => setSubStep('baselinker-import')}
        />
      )
    case 'baselinker-import':
      return (
        <BaselinkerImportSubStep
          onComplete={() => setSubStep('baselinker-mappings')}
          onSkip={() => setSubStep('baselinker-mappings')}
        />
      )
    case 'baselinker-mappings':
      return (
        <BaselinkerMappingsSubStep
          activeOrgId={activeOrgId ?? undefined}
          onComplete={onComplete}
          onBack={() => setSubStep('baselinker-import')}
        />
      )
    default:
      return (
        <StoreConnectScreen
          onConnected={() => setSubStep('order-source-choice')}
          onSkip={() => setSubStep('order-source-choice')}
        />
      )
  }
}

// =============================================================================
// ORDER SOURCE CHOICE
// =============================================================================

interface OrderSourceChoiceProps {
  hasShopifyStore: boolean
  shopifyStoreId: number | null
  onSelectDirect: () => void
  onSelectBaselinker: () => void
  onBack: () => void
  onSkip: () => void
}

function OrderSourceChoice({
  hasShopifyStore,
  shopifyStoreId,
  onSelectDirect,
  onSelectBaselinker,
  onBack,
  onSkip,
}: OrderSourceChoiceProps): ReactElement {
  const { t } = useTranslation('features-onboarding')
  const updateModeMutation = useUpdateConnectionModeMutation()

  const handleSelectDirect = async (): Promise<void> => {
    if (!shopifyStoreId) return
    try {
      await updateModeMutation.mutateAsync({ id: shopifyStoreId, connectionMode: 'DIRECT' })
      onSelectDirect()
    } catch {
      toast.error(t('salesChannel.orderSource.directError', 'Failed to enable direct sync'))
    }
  }

  return (
    <div className="max-w-2xl mx-auto text-center">
      <ScrollReveal animateOnMount blur>
        <h2 className="text-3xl font-bold tracking-tight text-foreground mb-4">
          {t('salesChannel.orderSource.title', 'How do you manage orders?')}
        </h2>
        <p className="text-muted-foreground mb-8">
          {t('salesChannel.orderSource.subtitle', 'Choose where your orders come from')}
        </p>
      </ScrollReveal>

      <ScrollReveal animateOnMount blur delay={0.1}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          {/* Shopify Direct Card */}
          <button
            type="button"
            onClick={handleSelectDirect}
            disabled={!hasShopifyStore || updateModeMutation.isPending}
            className="flex flex-col items-center gap-4 rounded-2xl border border-border/15 bg-card p-6 cursor-pointer transition-all hover:border-primary/30 hover:bg-card/80 text-left group disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-border/15 disabled:hover:bg-card"
          >
            <div className="w-16 h-16 rounded-2xl bg-[#96bf48]/10 border border-[#96bf48]/20 flex items-center justify-center group-hover:scale-105 transition-transform">
              <img src="/shopify.webp" alt="Shopify" className="w-10 h-10" />
            </div>
            <div className="text-center">
              <h3 className="font-semibold text-lg mb-1">
                {updateModeMutation.isPending ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t('salesChannel.orderSource.enabling', 'Enabling...')}
                  </span>
                ) : (
                  t('salesChannel.orderSource.direct.title', 'Directly from Shopify')
                )}
              </h3>
              <p className="text-sm text-muted-foreground">
                {hasShopifyStore
                  ? t('salesChannel.orderSource.direct.description', 'Sync orders directly from your Shopify store')
                  : t('salesChannel.orderSource.direct.noStore', 'Connect your Shopify store first')}
              </p>
            </div>
          </button>

          {/* BaseLinker Card */}
          <button
            type="button"
            onClick={onSelectBaselinker}
            className="flex flex-col items-center gap-4 rounded-2xl border border-border/15 bg-card p-6 cursor-pointer transition-all hover:border-primary/30 hover:bg-card/80 text-left group"
          >
            <div className="w-16 h-16 rounded-2xl bg-card border border-border/15 flex items-center justify-center group-hover:scale-105 transition-transform">
              <img src="/base-favicon.png" alt="BaseLinker" className="w-10 h-10" />
            </div>
            <div className="text-center">
              <h3 className="font-semibold text-lg mb-1">
                {t('salesChannel.orderSource.oms.title', 'Through an OMS')}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t('salesChannel.orderSource.oms.description', 'Connect BaseLinker to sync orders from multiple channels')}
              </p>
            </div>
          </button>
        </div>
      </ScrollReveal>

      <ScrollReveal animateOnMount blur delay={0.2}>
        <div className="flex items-center justify-center gap-4">
          <Button variant="ghost" onClick={onBack} className="opacity-50">
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            {t('common.back', 'Back')}
          </Button>
          <Button variant="ghost" onClick={onSkip} className="opacity-50">
            {t('salesChannel.skip', 'Skip for now')}
          </Button>
        </div>
      </ScrollReveal>
    </div>
  )
}

// =============================================================================
// SHOPIFY SUB-STEPS
// =============================================================================

interface StoreConnectScreenProps {
  onConnected: () => void
  onSkip: () => void
}

function StoreConnectScreen({ onConnected, onSkip }: StoreConnectScreenProps): ReactElement {
  const { t } = useTranslation('features-onboarding')

  return (
    <div className="max-w-md mx-auto text-center">
      <ScrollReveal animateOnMount blur>
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 rounded-2xl bg-[#96bf48]/10 border border-[#96bf48]/20 flex items-center justify-center">
            <img src="/shopify.webp" alt="Shopify" className="w-10 h-10" />
          </div>
        </div>
        <h2 className="text-3xl font-bold tracking-tight text-foreground mb-4">
          {t('salesChannel.shopify.connect.title', 'Connect your store')}
        </h2>
        <p className="text-muted-foreground mb-8">
          {t('salesChannel.shopify.connect.subtitle', 'Required for ad spend tracking across Google and Facebook')}
        </p>
      </ScrollReveal>

      <ScrollReveal animateOnMount blur delay={0.1}>
        <ShopifyOAuthConnect
          variant="card"
          connectionMode="ATTRIBUTION_ONLY"
          onConnected={onConnected}
          buttonLabel={t('salesChannel.shopify.connect.button', 'Connect Store')}
        />
      </ScrollReveal>

      <ScrollReveal animateOnMount blur delay={0.2}>
        <div className="mt-6">
          <Button variant="ghost" onClick={onSkip} className="opacity-50">
            {t('salesChannel.shopify.connect.noStore', "I don't have a Shopify store")}
          </Button>
        </div>
      </ScrollReveal>
    </div>
  )
}

interface ShopifyImportSubStepProps {
  onComplete: () => void
  onBack: () => void
  onSkip: () => void
}

function toImportVariant(variant: ShopifyVariantPreviewItem): ImportVariantItem {
  return {
    variantId: variant.variantId,
    name: variant.title,
    sku: variant.sku,
    imageUrl: variant.imageUrl ?? null,
    alreadyExists: variant.alreadyExists,
    cost: variant.cost ?? null,
    costCurrency: variant.costCurrency ?? null,
  }
}

function toImportProduct(product: ShopifyProductPreviewItem): ImportProductItem {
  return {
    externalProductId: product.shopifyProductId,
    productName: product.productName,
    imageUrl: product.imageUrl ?? null,
    sku: null,
    skuAlreadyExists: false,
    allVariantsExist: product.allVariantsExist,
    hasOnlyDefaultVariant: product.hasOnlyDefaultVariant ?? false,
    variantCount: product.variantCount,
    variants: product.variants?.map(toImportVariant) ?? null,
    averageCost: null,
    siblingConflict: product.siblingConflict ?? false,
    siblingConflictDetails: product.siblingConflictDetails ?? null,
  }
}

function ShopifyImportSubStep({ onComplete, onBack, onSkip }: ShopifyImportSubStepProps): ReactElement {
  const { t } = useTranslation('features-onboarding')

  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set())
  const [selectedVariantIds, setSelectedVariantIds] = useState<Set<string>>(new Set())
  const [searchTerm, setSearchTerm] = useState('')

  const storesQuery = useShopifyStoresQuery(true)
  const stores = storesQuery.data ?? []
  const connectionId = stores[0]?.connectionId ?? null

  const previewQuery = useShopifyImportPreviewQuery(connectionId, connectionId !== null)
  const importMutation = useShopifyImportProductsMutation()

  const allProducts = useMemo(() => {
    if (!previewQuery.data) return []
    return [
      ...previewQuery.data.newProducts,
      ...previewQuery.data.alreadyImportedProducts,
    ].map(toImportProduct)
  }, [previewQuery.data])

  const handleSelectAllNew = useCallback((): void => {
    const newProductIds = new Set<string>()
    allProducts.forEach(p => {
      if (!p.allVariantsExist && !p.siblingConflict) {
        newProductIds.add(p.externalProductId)
      }
    })
    setSelectedProductIds(newProductIds)
    setSelectedVariantIds(new Set())
  }, [allProducts])

  const handleImport = async (): Promise<void> => {
    if (!connectionId || selectedProductIds.size === 0) return

    try {
      await importMutation.mutateAsync({
        connectionId,
        request: {
          productIds: Array.from(selectedProductIds),
        },
      })
      toast.success(t('salesChannel.shopify.import.success', 'Products imported successfully!'))
      onComplete()
    } catch {
      toast.error(t('salesChannel.shopify.import.error', 'Failed to import products'))
    }
  }

  const totalSelected = selectedProductIds.size + selectedVariantIds.size

  return (
    <div className="max-w-4xl mx-auto">
      <ScrollReveal animateOnMount blur>
        <h2 className="text-3xl font-bold tracking-tight text-foreground mb-4 text-center">
          {t('salesChannel.shopify.import.title', 'Import Products')}
        </h2>
        <p className="text-muted-foreground mb-8 text-center">
          {t('salesChannel.shopify.import.subtitle', 'Select products to import from Shopify')}
        </p>
      </ScrollReveal>

      <ScrollReveal animateOnMount blur delay={0.1}>
        <div className="bg-card rounded-2xl border border-border/15 overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b border-border/15 flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('salesChannel.shopify.import.searchPlaceholder', 'Search products...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button variant="outline" onClick={handleSelectAllNew}>
              {t('salesChannel.shopify.import.selectAllNew', 'Select all new')}
            </Button>
          </div>

          {/* Products table */}
          <div className="min-h-[320px] sm:min-h-[400px]">
            <ProductImportTable
              products={allProducts}
              selectedProductIds={selectedProductIds}
              selectedVariantIds={selectedVariantIds}
              onProductSelectionChange={setSelectedProductIds}
              onVariantSelectionChange={setSelectedVariantIds}
              isActive
              isLoading={storesQuery.isLoading || previewQuery.isLoading}
              isError={previewQuery.isError}
              showStatusColumn
              searchTerm={searchTerm}
              noBorder
              maxHeight="400px"
              sourceName="Shopify"
              getVariantIdentifier={(v) => v.variantId}
              {...(previewQuery.data?.totalProducts !== undefined && { totalProducts: previewQuery.data.totalProducts })}
              {...(previewQuery.data?.alreadyImported !== undefined && { alreadyImported: previewQuery.data.alreadyImported })}
            />
          </div>

          {/* Action buttons */}
          <div className="p-4 border-t border-border/15 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={onBack}>
                <ArrowLeft className="h-4 w-4 mr-1.5" />
                {t('common.back', 'Back')}
              </Button>
              <Button variant="ghost" onClick={onSkip} className="opacity-50">
                {t('salesChannel.shopify.import.skip', 'Skip import')}
              </Button>
            </div>
            <Button
              variant="outline"
              size="lg"
              onClick={handleImport}
              disabled={totalSelected === 0 || importMutation.isPending}
            >
              {importMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  {t('salesChannel.shopify.import.importing', 'Importing...')}
                </>
              ) : (
                <>
                  {t('salesChannel.shopify.import.importSelected', { count: totalSelected, defaultValue: `Import ${totalSelected} products` })}
                  <ArrowRight className="h-4 w-4 ml-1.5" />
                </>
              )}
            </Button>
          </div>
        </div>
      </ScrollReveal>
    </div>
  )
}

// =============================================================================
// BASELINKER SUB-STEPS
// =============================================================================

interface BaselinkerGuideSubStepProps {
  onContinue: () => void
  onBack: () => void
  onSkip: () => void
}

function BaselinkerGuideSubStep({ onContinue, onBack, onSkip }: BaselinkerGuideSubStepProps): ReactElement {
  const { t } = useTranslation('features-integrations')

  return (
    <div className="max-w-4xl mx-auto text-center">
      <ScrollReveal animateOnMount blur>
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 rounded-2xl bg-card border border-border/15 flex items-center justify-center">
            <img src="/base-favicon.png" alt="BaseLinker" className="w-10 h-10" />
          </div>
        </div>
        <h2 className="text-3xl font-bold tracking-tight text-foreground mb-4 text-center">
          {t('setup.title', 'Connect your sales channel')}
        </h2>
        <p className="text-muted-foreground mb-8 text-center">
          {t('setup.subtitle', 'Import orders from BaseLinker')}
        </p>
      </ScrollReveal>

      <ScrollReveal animateOnMount blur delay={0.1}>
        <div className="bg-card rounded-2xl border border-border/15 p-4 sm:p-6 space-y-4 sm:space-y-6">
          <h3 className="font-semibold">
            {t('setup.steps.guide.title', 'Find your API Token')}
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-4">
            <div className="flex flex-col gap-3">
              <div className="flex items-start gap-2 order-first sm:order-last">
                <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold">
                  1
                </div>
                <span className="text-sm text-muted-foreground text-left">
                  {t('setup.steps.guide.step1', 'Click profile → "My Account"')}
                </span>
              </div>
              <div className="rounded-lg border border-border/30 overflow-hidden">
                <img src="/base-integration-guide/bs-step1.webp" alt="Step 1" className="w-full" />
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <div className="flex items-start gap-2 order-first sm:order-last">
                <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold">
                  2
                </div>
                <span className="text-sm text-muted-foreground text-left">
                  {t('setup.steps.guide.step2', 'Go to the "API" tab')}
                </span>
              </div>
              <div className="rounded-lg border border-border/30 overflow-hidden">
                <img src="/base-integration-guide/bs-step2.webp" alt="Step 2" className="w-full" />
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <div className="flex items-start gap-2 order-first sm:order-last">
                <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold">
                  3
                </div>
                <span className="text-sm text-muted-foreground text-left">
                  {t('setup.steps.guide.step3', 'Copy your API token')}
                </span>
              </div>
              <div className="rounded-lg border border-border/30 overflow-hidden">
                <img src="/base-integration-guide/bs-step3.webp" alt="Step 3" className="w-full" />
              </div>
            </div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-4 border-t border-border/40">
            <div className="flex items-center gap-3">
              <Button variant="outline" size="lg" onClick={onBack}>
                <ArrowLeft className="h-4 w-4 mr-1.5" />
                {t('common.back', 'Back')}
              </Button>
              <a
                href="https://panel.baselinker.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="outline" size="lg" className="text-muted-foreground">
                  {t('setup.steps.guide.openBaselinker', 'Open Baselinker')}
                  <ExternalLink className="h-3 w-3 ml-1.5" />
                </Button>
              </a>
            </div>
            <Button variant="outline" size="lg" onClick={onContinue}>
              {t('setup.steps.guide.continue', 'I have my token')}
              <ArrowRight className="h-3 w-3 ml-1.5" />
            </Button>
          </div>
        </div>
      </ScrollReveal>

      <ScrollReveal animateOnMount blur delay={0.2}>
        <div className="mt-6 text-center">
          <Button variant="ghost" onClick={onSkip} className="opacity-50">
            {t('setup.skip', 'Skip for now')}
          </Button>
        </div>
      </ScrollReveal>
    </div>
  )
}

interface BaselinkerConnectSubStepProps {
  onConnected: () => void
  onBack: () => void
}

function BaselinkerConnectSubStep({ onConnected, onBack }: BaselinkerConnectSubStepProps): ReactElement {
  const { t } = useTranslation('features-integrations')
  const { activeOrgId } = useActiveOrg()
  const queryClient = useQueryClient()

  const [token, setToken] = useState('')
  const [showToken, setShowToken] = useState(false)

  const connectMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      if (!activeOrgId) throw new Error('No active organization')
      const orgId = OrgIdSchema.parse(activeOrgId) as unknown as string
      const payload = BaseLinkerCredentialsSchema.parse({ blApiToken: token })
      await apiClient.put(`/orgs/${orgId}/integrations/BASELINKER/credentials`, payload)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['integrations', 'status'] })
      onConnected()
    },
    onError: (e: unknown) => {
      toast.error((e as Error)?.message ?? t('setup.steps.connect.error', 'Failed to connect'))
    },
  })

  const handleConnect = (): void => {
    if (token.trim().length === 0) return
    connectMutation.mutate()
  }

  return (
    <div className="max-w-md mx-auto">
      <ScrollReveal animateOnMount blur>
        <div className="bg-card rounded-2xl border border-border/15 p-6 space-y-6">
          <h3 className="font-semibold">
            {t('setup.steps.connect.title', 'Connect')}
          </h3>

          <div>
            <div className="relative">
              <Input
                id="api-token"
                type={showToken ? 'text' : 'password'}
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder={t('setup.steps.connect.placeholder', 'Enter your Baselinker API token')}
                className="pr-10"
                autoComplete="off"
                data-1p-ignore
                data-lpignore="true"
                data-form-type="other"
              />
              <button
                type="button"
                onClick={() => setShowToken(prev => !prev)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {t('setup.steps.connect.hint', 'Your token is stored securely')}
            </p>
          </div>

          <div className="flex items-center gap-3 pt-4 border-t border-border/40">
            <Button variant="outline" size="lg" onClick={onBack}>
              <ArrowLeft className="h-4 w-4 mr-1.5" />
              {t('common.back', 'Back')}
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={handleConnect}
              disabled={token.trim().length === 0 || connectMutation.isPending}
              className="flex-1"
            >
              {connectMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  {t('setup.steps.connect.connecting', 'Connecting...')}
                </>
              ) : (
                <>
                  {t('setup.steps.connect.button', 'Connect')}
                  <ArrowRight className="h-4 w-4 ml-1.5" />
                </>
              )}
            </Button>
          </div>
        </div>
      </ScrollReveal>
    </div>
  )
}

interface BaselinkerCurrencySubStepProps {
  activeOrgId: string | undefined
  onSaved: () => void
}

function BaselinkerCurrencySubStep({ activeOrgId, onSaved }: BaselinkerCurrencySubStepProps): ReactElement {
  const { t } = useTranslation('features-integrations')
  const queryClient = useQueryClient()

  const [selectedCurrency, setSelectedCurrency] = useState<string | null>(null)

  const orgSettingsQuery = useQuery({
    queryKey: activeOrgId ? ['org', activeOrgId, 'settings'] : ['settings', 'no-org'],
    queryFn: () => getOrgSettings(OrgIdSchema.parse(activeOrgId)),
    enabled: !!activeOrgId,
    staleTime: 5 * 60 * 1000,
  })

  const currencyQuery = useQuery({
    queryKey: ['baselinker', 'currency'],
    queryFn: getBaselinkerCurrency,
    staleTime: 5 * 60 * 1000,
  })

  const currencyMutation = useMutation({
    mutationFn: setBaselinkerCurrency,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['baselinker', 'currency'] })
      onSaved()
    },
    onError: () => {
      toast.error(t('setup.steps.currency.error', 'Failed to save currency'))
    },
  })

  const effectiveSelectedCurrency = selectedCurrency ?? currencyQuery.data?.currency ?? null
  const allowedCurrencies = orgSettingsQuery.data?.allowedReportingCurrencies ?? []

  const handleCurrencySave = (): void => {
    if (!effectiveSelectedCurrency) return
    currencyMutation.mutate(effectiveSelectedCurrency)
  }

  return (
    <div className="max-w-md mx-auto">
      <ScrollReveal animateOnMount blur>
        <div className="bg-card rounded-2xl border border-border/15 p-6 space-y-6">
          <h3 className="font-semibold">
            {t('setup.steps.currency.title', 'Select BaseLinker account currency')}
          </h3>

          {orgSettingsQuery.isLoading ? (
            <Skeleton className="h-10 w-full" />
          ) : (
            <Select
              value={effectiveSelectedCurrency ?? ''}
              onValueChange={setSelectedCurrency}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('setup.steps.currency.placeholder', 'Select currency')} />
              </SelectTrigger>
              <SelectContent>
                {allowedCurrencies.map(code => (
                  <SelectItem key={code} value={code}>
                    {code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <div className="pt-4 border-t border-border/40">
            <Button
              variant="outline"
              size="lg"
              onClick={handleCurrencySave}
              disabled={!effectiveSelectedCurrency || currencyMutation.isPending}
              className="w-full"
            >
              {currencyMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  {t('setup.steps.currency.saving', 'Saving...')}
                </>
              ) : (
                <>
                  {t('setup.steps.currency.continue', 'Continue')}
                  <ArrowRight className="h-4 w-4 ml-1.5" />
                </>
              )}
            </Button>
          </div>
        </div>
      </ScrollReveal>
    </div>
  )
}

interface BaselinkerImportSubStepProps {
  onComplete: () => void
  onSkip: () => void
}

function BaselinkerImportSubStep({ onComplete, onSkip }: BaselinkerImportSubStepProps): ReactElement {
  const { t } = useTranslation('features-integrations')

  const [selectedInventoryId, setSelectedInventoryId] = useState<number | null>(null)
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set())
  const [selectedVariantSkus, setSelectedVariantSkus] = useState<Set<string>>(new Set())
  const [searchTerm, setSearchTerm] = useState('')

  const inventoriesQuery = useBaselinkerInventoriesQuery(true)
  const firstInventoryId = inventoriesQuery.data?.[0]?.inventoryId ?? null
  const effectiveInventoryId = selectedInventoryId ?? firstInventoryId

  const previewQuery = useImportPreviewQuery(effectiveInventoryId, effectiveInventoryId !== null)
  const importMutation = useImportProductsMutation()

  const currencyQuery = useQuery({
    queryKey: ['baselinker', 'currency'],
    queryFn: getBaselinkerCurrency,
    staleTime: 5 * 60 * 1000,
  })

  const allProducts = previewQuery.data?.newProducts ?? []

  const handleSelectAll = (): void => {
    const newProductIds = new Set<string>()
    const newVariantSkus = new Set<string>()

    allProducts.forEach(p => {
      const importableVariants = p.variants?.filter(v => !v.alreadyExists) ?? []
      const hasVariants = (p.variants?.length ?? 0) > 0

      if (!hasVariants && !p.skuAlreadyExists) {
        newProductIds.add(p.baselinkerProductId)
      } else if (importableVariants.length === p.variants?.length) {
        newProductIds.add(p.baselinkerProductId)
      } else if (importableVariants.length > 0) {
        importableVariants.forEach(v => newVariantSkus.add(v.sku))
      }
    })

    setSelectedProductIds(newProductIds)
    setSelectedVariantSkus(newVariantSkus)
  }

  const handleClearAll = (): void => {
    setSelectedProductIds(new Set())
    setSelectedVariantSkus(new Set())
  }

  const handleImport = async (): Promise<void> => {
    if (!effectiveInventoryId || (selectedProductIds.size === 0 && selectedVariantSkus.size === 0)) return

    try {
      await importMutation.mutateAsync({
        inventoryId: effectiveInventoryId,
        productIds: selectedProductIds.size > 0 ? Array.from(selectedProductIds) : undefined,
        variantSkus: selectedVariantSkus.size > 0 ? Array.from(selectedVariantSkus) : undefined,
      })

      // Sync is triggered in the mappings step after status configuration
      onComplete()
    } catch {
      // Error handled by mutation
    }
  }

  const handleSkipImport = (): void => {
    // Sync is triggered in the mappings step after status configuration
    onSkip()
  }

  const totalSelected = selectedProductIds.size + selectedVariantSkus.size

  return (
    <div className="max-w-4xl mx-auto">
      <ScrollReveal animateOnMount blur>
        <h2 className="text-3xl font-bold tracking-tight text-foreground mb-4 text-center">
          {t('setup.steps.import.title', 'Import Products')}
        </h2>
        <p className="text-muted-foreground mb-8 text-center">
          {t('setup.steps.import.subtitle', 'Import products from Baselinker catalog')}
        </p>
      </ScrollReveal>

      <ScrollReveal animateOnMount blur delay={0.1}>
        <div className="bg-card rounded-2xl border border-border/15 overflow-hidden">
          <div className="p-3 sm:p-4 border-b border-border/15 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <Select
              value={effectiveInventoryId?.toString() ?? ''}
              onValueChange={(val) => {
                setSelectedInventoryId(parseInt(val, 10))
                setSelectedProductIds(new Set())
                setSelectedVariantSkus(new Set())
                setSearchTerm('')
              }}
            >
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder={t('setup.steps.import.selectCatalog', 'Select catalog')} />
              </SelectTrigger>
              <SelectContent>
                {inventoriesQuery.data?.map(inv => (
                  <SelectItem key={inv.inventoryId} value={inv.inventoryId.toString()}>
                    {inv.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('setup.steps.import.searchPlaceholder', 'Search products...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          <div className="min-h-[320px] sm:min-h-[460px]">
            <BaselinkerProductsTable
              products={allProducts}
              currency={currencyQuery.data?.currency ?? null}
              selectedProductIds={selectedProductIds}
              selectedVariantSkus={selectedVariantSkus}
              onProductSelectionChange={setSelectedProductIds}
              onVariantSelectionChange={setSelectedVariantSkus}
              isActive
              isLoading={inventoriesQuery.isLoading || previewQuery.isLoading}
              isError={previewQuery.isError}
              enableVariantSelection
              searchTerm={searchTerm}
              noBorder
              maxHeight="460px"
              {...(previewQuery.data?.totalProducts !== undefined && { totalProducts: previewQuery.data.totalProducts })}
              {...(previewQuery.data?.alreadyImported !== undefined && { alreadyImported: previewQuery.data.alreadyImported })}
              {...(allProducts.length > 0 && { onSelectAll: handleSelectAll })}
              onClearAll={handleClearAll}
            />
          </div>

          <div className="p-3 sm:p-4 border-t border-border/15 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3">
            <Button variant="ghost" onClick={handleSkipImport} className="opacity-50">
              {t('setup.steps.import.skip', 'Skip for now')}
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={handleImport}
              disabled={totalSelected === 0 || importMutation.isPending}
              className="w-full sm:w-auto"
            >
              {importMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  {t('setup.steps.import.importing', 'Importing...')}
                </>
              ) : (
                <>
                  {t('setup.steps.import.importSelected', { count: totalSelected, defaultValue: `Import ${totalSelected} products` })}
                  <ArrowRight className="h-4 w-4 ml-1.5" />
                </>
              )}
            </Button>
          </div>
        </div>
      </ScrollReveal>
    </div>
  )
}

interface BaselinkerMappingsSubStepProps {
  activeOrgId: string | undefined
  onComplete: () => void
  onBack: () => void
}

interface StatusRow {
  id: number
  externalStatus: string
  statusName: string | null
  viewType: ViewType | null
}

function BaselinkerMappingsSubStep({ activeOrgId, onComplete, onBack }: BaselinkerMappingsSubStepProps): ReactElement {
  const { t } = useTranslation('features-integrations')
  const queryClient = useQueryClient()

  const handleContinue = (): void => {
    // Enable sync and trigger initial historical sync now that mappings are configured
    if (activeOrgId) {
      apiClient.post(`/orgs/${activeOrgId}/integrations/BASELINKER/enable-sync`)
        .then(() => {
          toast.success(t('setup.steps.mappings.syncStarted', 'Syncing your order data...'))
        })
        .catch(() => {
          // Non-blocking - continue even if this fails
        })
    }
    onComplete()
  }

  const mappingsQuery = useQuery({
    queryKey: ['status-mappings'],
    queryFn: () => listStatusMappings(),
    staleTime: 60_000,
  })

  const updateMutation = useMutation({
    mutationFn: (params: { id: number; viewType: ViewType }) =>
      updateStatusMapping(params.id, { viewType: params.viewType }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['status-mappings'] })
    },
  })

  const allRows: StatusRow[] = (mappingsQuery.data ?? [])
    .map((m) => ({
      id: m.id,
      externalStatus: m.externalStatus,
      statusName: m.statusName ?? null,
      viewType: m.viewType,
    }))
    .sort((a, b) => {
      const nameA = a.statusName ?? a.externalStatus
      const nameB = b.statusName ?? b.externalStatus
      return nameA.localeCompare(nameB)
    })

  const handleMappingChange = (row: StatusRow, newViewType: ViewType): void => {
    updateMutation.mutate({ id: row.id, viewType: newViewType })
  }

  const isLoading = mappingsQuery.isLoading

  return (
    <div className="max-w-2xl mx-auto">
      <ScrollReveal animateOnMount blur>
        <div className="bg-card rounded-2xl border border-border/15 p-6 space-y-6">
          <div>
            <h3 className="font-semibold mb-2">
              {t('setup.steps.mappings.title', 'How to treat your statuses?')}
            </h3>
            <div className="text-sm text-muted-foreground space-y-1">
              <p>{t('setup.steps.mappings.hint.cancelled', 'Cancelled — treated as cancelled orders and returns')}</p>
              <p>{t('setup.steps.mappings.hint.sales', 'Sales — treated as revenue')}</p>
              <p>{t('setup.steps.mappings.hint.ignore', 'Ignore — not included in statistics at all')}</p>
            </div>
          </div>

          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : allRows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">
              {t('setup.steps.mappings.noStatuses', 'No order statuses found. Sync some orders first.')}
            </p>
          ) : (
            <div className="rounded-xl">
              <div className="grid grid-cols-[1fr_auto_1fr] gap-4 pb-3 border-b border-border/40 mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t('setup.steps.mappings.baselinkerStatus', 'Baselinker Status')}
                </span>
                <span />
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t('setup.steps.mappings.treatAs', 'Treat as')}
                </span>
              </div>

              {allRows.map((row) => (
                <div
                  key={row.externalStatus}
                  className="grid grid-cols-[1fr_auto_1fr] gap-4 items-center py-3 border-b border-border/20 last:border-b-0"
                >
                  <span className="text-sm truncate" title={row.externalStatus}>
                    {row.statusName ?? row.externalStatus}
                  </span>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  <Select
                    value={row.viewType ?? ''}
                    onValueChange={(val) => handleMappingChange(row, val as ViewType)}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder={t('setup.steps.mappings.selectStatus', 'Select...')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SALES_ANALYTICS">{t('setup.steps.mappings.options.sales', 'Sales')}</SelectItem>
                      <SelectItem value="CANCELLATION_ANALYTICS">{t('setup.steps.mappings.options.cancelled', 'Cancelled')}</SelectItem>
                      <SelectItem value="AUDIT">{t('setup.steps.mappings.options.ignore', 'Ignore')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center gap-3 pt-4 border-t border-border/40">
            <Button variant="outline" size="lg" onClick={onBack}>
              <ArrowLeft className="h-4 w-4 mr-1.5" />
              {t('common.back', 'Back')}
            </Button>
            <Button variant="outline" size="lg" onClick={handleContinue} className="flex-1">
              {t('setup.steps.mappings.continue', 'Continue')}
              <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          </div>
        </div>
      </ScrollReveal>
    </div>
  )
}
